import React, { useState, useEffect } from "react";
import "./ReportTableRow.css";
import Switch from "react-switch";
import {
  upsertSiFcReport,
  getSiFcReportByStudentId,
  updateStudent,
  addStudentToWatchlist,
  deleteWatchlistStudent,
} from "../services/APIClient";

// Define a constant for the image path base to make it easier to update
const IMAGE_PATH = process.env.PUBLIC_URL + "/assets/imgs/";

function SiFcReportTableRow({
  student,
  dummyHeaderArray,
  getReadingScoreClass,
  handleShowReadingScoreModal,
  handleShowLiteracyModal,
  handleShowAdditionalNeedsModal,
  handleShowSupportPlanModal,
  handleShowEalModal,
  handleShowSetModal,
  handleShowAttendanceAffectingScoresModal,
  handleShowConfirmationModalEAL,
  handleShowConfirmationModalSET,
  handleShowConfirmationModalAttendance,
  setSaveClicked,
  teacherid,
  classId,
  setSortedData,
  sortStudents,
  sortCriteria,
  additionalRoles,
  saveClicked // Add this prop
}) {
  const [readingScoreNotes, setReadingScoreNotes] = useState([]);
  const [literacyObservations, setLiteracyObservations] = useState("");
  const [additionalNeeds, setAdditionalNeeds] = useState([]);
  const [supportPlan, setSupportPlan] = useState([]);
  const [eal, setEal] = useState(false);
  const [set, setSet] = useState(false);
  const [attendanceAffectingScores, setAttendanceAffectingScores] =
    useState(false);

  // Removed button clicked state variables as they're no longer needed

  const [currentStudentId, setCurrentStudentId] = useState("");

  // Function to check if the user has SENCO or SET role
  const userIsSencoOrSet = () => {
    if (!additionalRoles || !Array.isArray(additionalRoles)) return false;

    // Check if "SENCO" or "SET" exists in the additionalRoles array (case insensitive)
    return additionalRoles.some(role =>
      typeof role === "string" &&
      (role.toUpperCase() === "SENCO" || role.toUpperCase() === "SET")
    );
  };

  // UseEffect to log currentStudentId when it changes
  useEffect(() => {
    console.log(currentStudentId);
  }, [currentStudentId]);

  // Add effect to log when saveClicked changes
  useEffect(() => {
    console.log("SiFcReportTableRow: saveClicked changed to:", saveClicked);
  }, [saveClicked]);

  // Fetch SiFc report when student id changes or when saveClicked changes
  useEffect(() => {
    if (student.id) {
      console.log(`Fetching SiFc report for student: ${student.id} (saveClicked: ${saveClicked})`);
      getSiFcReportByStudentId(student.id)
        .then((response) => {
          console.log(`Report fetched successfully for student: ${student.id}`);
          if (response) {
            setReadingScoreNotes(response.readingscorenotes || []);
            setLiteracyObservations(response.literacyobservations || "");
            setAdditionalNeeds(response.additionalneeds || []);
            setSupportPlan(response.supportplan || []);
            setEal(
              response.eal !== undefined ? response.eal : student.esl || false
            );
            setSet(response.set || false);
            setAttendanceAffectingScores(
              response.attendanceaffectingscores || false
            );
          } else {
            setEal(student.esl || false);
            setReadingScoreNotes([]);
            setLiteracyObservations("");
            setAdditionalNeeds([]);
            setSupportPlan([]);
            setSet(false);
            setAttendanceAffectingScores(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching SiFc report:", error);
          setEal(student.esl || false);
        });
    }
  }, [student.id, student.esl, saveClicked]); // Added saveClicked to the dependency array

  useEffect(() => {
    const saveReport = () => {
      if (currentStudentId) {
        const reportData = {
          studentid: currentStudentId,
          teacherid: teacherid,
          set: set,
          attendanceaffectingscores: attendanceAffectingScores,
        };

        console.log("Saving report data (SET/Attendance):", reportData);

        upsertSiFcReport(reportData)
          .then((response) => {
            console.log(
              "Upsert SiFc Report Success (SET/Attendance):",
              response
            );
          })
          .catch((error) => console.error("Error:", error));
      } else {
        console.log(
          "No student selected. Skipping save operation (SET/Attendance)."
        );
      }
    };

    if (currentStudentId) {
      saveReport();
    }
  }, [set, attendanceAffectingScores, currentStudentId, teacherid]);

  const handleEalToggle = async (studentId, currentEalValue) => {
    const newEalValue = !currentEalValue;
    setEal(newEalValue);

    const reportData = {
      studentid: studentId,
      teacherid: teacherid,
      eal: newEalValue,
    };

    const studentUpdateData = {
      esl: newEalValue,
    };

    try {
      await upsertSiFcReport(reportData);
      console.log("Upsert SiFc Report Success for EAL:", newEalValue);

      await updateStudent(studentId, studentUpdateData);
      console.log("Update Student Success for ESL sync:", newEalValue);
    } catch (error) {
      console.error("Error updating EAL/ESL state:", error);
      setEal(currentEalValue);
    }
  };

  const handleSenToggle = async (studentId, currentSenState, classId) => {
    const updatedSenState = !currentSenState;

    if (!classId) {
      console.error("classId is required to add student to the watchlist.");
      return;
    }

    try {
      await updateStudent(studentId, { sen: updatedSenState });

      if (updatedSenState) {
        const data = { studentid: studentId, classid: classId };
        await addStudentToWatchlist(data);
        console.log(`Student ${studentId} added to watchlist successfully!`);
      } else {
        await deleteWatchlistStudent(studentId);
        console.log(
          `Student ${studentId} removed from watchlist successfully!`
        );
      }

      setSortedData((prevSortedData) =>
        sortStudents(
          prevSortedData.map((student) =>
            student.id === studentId
              ? { ...student, sen: updatedSenState }
              : student
          ),
          sortCriteria
        )
      );
    } catch (error) {
      console.error("Error updating SEN state:", error);
    }
  };

  return (
    <tr
      key={student.id}
      className="table-row student-results-table-row"
      data-student-id={student.id}
    >
      <td className="centre-student-items align-left">{student.name}</td>
      <td className="centre-student-items task-badge-parent tooltip-container">
        {student.readingScore !== undefined && student.readingScore !== null ? (
          student.readingScore === "No completed tasks" ? (
            <span className="reading-score">{student.readingScore}</span>
          ) : (
            <span
              className={`reading-score ${getReadingScoreClass(
                student.readingScore
              )}`}
            >
              {student.readingScore}%
            </span>
          )
        ) : null}
        <span className="tooltip">
          Average of all this years screening points
        </span>
      </td>
      {dummyHeaderArray.map((header, index) => (
        <td key={index} className="centre-student-items">
          {header === "Reading Score Observations" ? (
            <img
              src={
                readingScoreNotes.length > 0
                  ? `${IMAGE_PATH}file-pen-outline.svg`
                  : `${IMAGE_PATH}file-outline.svg`
              }
              alt="Icon"
              style={{ cursor: "pointer" }}
              onClick={() => {
                handleShowReadingScoreModal(student.name, student.id);
              }}
            />
          ) : header === "Literacy Observations" ? (
            <img
              src={
                literacyObservations && literacyObservations.length > 0
                  ? `${IMAGE_PATH}file-pen-outline.svg`
                  : `${IMAGE_PATH}file-outline.svg`
              }
              alt="Icon"
              style={{ cursor: "pointer" }}
              onClick={() => {
                handleShowLiteracyModal(student.name, student.id);
              }}
            />
          ) : header === "AEN Observations" ? (
            <img
              src={
                additionalNeeds.length > 0
                  ? `${IMAGE_PATH}file-pen-outline.svg`
                  : `${IMAGE_PATH}file-outline.svg`
              }
              alt="Icon"
              style={{ cursor: "pointer" }}
              onClick={() => {
                handleShowAdditionalNeedsModal(student.name, student.id);
              }}
            />
          ) : header === "Classroom Support Observations" ? (
            <img
              src={
                supportPlan.length > 0
                  ? `${IMAGE_PATH}file-pen-outline.svg`
                  : `${IMAGE_PATH}file-outline.svg`
              }
              alt="Icon"
              style={{ cursor: "pointer" }}
              onClick={() => {
                handleShowSupportPlanModal(student.name, student.id);
              }}
            />
          ) : header === "EAL" ? (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <input
                type="checkbox"
                checked={eal}
                onChange={() => {
                  handleEalToggle(student.id, eal);
                }}
                style={{
                  appearance: "none",
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  border: "2px solid #2c7693",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              />
              {eal && (
                <img
                  src={`${IMAGE_PATH}greenTick.svg`}
                  alt="Checked"
                  style={{ width: "15px", position: "absolute" }}
                />
              )}
            </label>
          ) : header === "SEN" ? (
            <>
              <div
                style={{
                  display: "inline-block",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    position: "relative",
                  }}
                  className="switch-container"
                >
                  <Switch
                    onChange={() => {
                      // Allow toggle in both directions if user is SENCO or SET
                      if (!student.sen || userIsSencoOrSet()) {
                        handleSenToggle(student.id, student.sen, classId);
                      }
                    }}
                    checked={!!student.sen}
                    height={20}
                    width={40}
                    onColor="#1A56DB"
                    offColor="#88888899"
                    activeBoxShadow="0 0 2px 3px #1A56DB"
                    handleDiameter={18}
                    disabled={student.sen && !userIsSencoOrSet()} // Only disable if ON and user isn't SENCO/SET
                  />
                  {student.sen ? (
                    <span className="tooltip">
                      {userIsSencoOrSet()
                        ? "Click to remove the child from the watchlist"
                        : "only a SET or SENCO can remove the child from the watchlist"}
                    </span>
                  ) : (
                    <span className="tooltip">
                      If concerned, add child to the watchlist to discuss with SEN Team.
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : header === "Attendance Affecting Scores" ? (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <input
                type="checkbox"
                checked={attendanceAffectingScores}
                onChange={() => {
                  setCurrentStudentId(student.id);
                  setAttendanceAffectingScores((prev) => !prev);
                }}
                style={{
                  appearance: "none",
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  border: "2px solid #2c7693",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              />
              {attendanceAffectingScores && (
                <img
                  src={`${IMAGE_PATH}greenTick.svg`}
                  alt="Checked"
                  style={{ width: "15px", position: "absolute" }}
                />
              )}
            </label>
          ) : (
            <button className="white-button">
              <i className="bi bi-pencil-square"></i>
            </button>
          )}
        </td>
      ))}
    </tr>
  );
}

export default React.memo(SiFcReportTableRow);